import * as React from 'react';
import { Link } from 'react-router-dom';
import { media } from '@minecraft.themes';
import Button from '@minecraft.button';
import Icon from '@minecraft.icon';
import { useRouteParams } from '@minecraft.utils';
import styled from 'styled-components';
import { SCREEN_NAMES, useTextHighligher } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import { AnchorButton, UserInfoContainer, InfoHeading, WithIsPreview } from '../styles/ProfileView';
import ProfileViewContext from '../ProfileViewContext';

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);

const sectionName = 'other-experience';

const UserInfoBtnContainer = styled.div`
  background: ${(props) => props.theme.colors.accent.greyBlue2};
  padding: 1rem 1rem 1rem 3rem;
  display: flex;
  justify-content: center;
  align-items: baseline;
  margin: 0 1rem;
  ${media.sm`
    margin: 0;
    justify-content: flex-start;
  `}
`;

const UserInfoSection = styled.div`
  background: ${(props) => props.theme.colors.primary.white};
  padding: 1rem;
  ${media.sm`
    padding: 1rem 3rem;
  `}
`;

const UserInfoHeading = styled.h3<WithIsPreview>`
  font-size: 1.7rem;
  line-height: 2rem;
  margin-bottom: ${(props) => (props.isPreview ? '0' : '1rem')};
  ${media.sm`
    margin-bottom: 1rem;
  `}
`;

const UserInfoHeadingContainer = styled.div<WithIsPreview>`
  display: flex;
  justify-content: space-between;
  @media screen and (max-width: 767px) {
    border-bottom: ${(props) => (props.isPreview ? `0.0625rem solid ${props.theme.colors.primary.ltGrey1}` : '0')};
    margin-bottom: ${(props) => (props.isPreview ? '1.5rem' : '0')};
    padding-bottom: ${(props) => (props.isPreview ? '1rem' : '0')};
    align-items: ${(props) => (props.isPreview ? 'center' : 'flex-start')};
  }
`;

const WrappedTextContainer = styled.div`
  overflow-wrap: break-word;
  white-space: pre-wrap;
`;

const StyledIcon = styled(Icon)`
  width: 1.0625rem;
  visibility: visible;
  ${media.sm`
    visibility: hidden;
  `}
`;

const OtherExperience = () => {
  const { 'other-experience': otherExperienceQueryString } = useRouteParams<{
    'other-experience': string;
  }>();

  const { t } = useTranslation();

  const [openSection, setSectionState] = React.useState(true);
  const {
    fetcher: { profile },
    isPreview,
    isAdmin,
    onEditClick,
  } = React.useContext(ProfileViewContext);

  const contentRef = useTextHighligher(otherExperienceQueryString);
  const otherExperiencesButton = isAdmin ? (
    <AnchorButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-edit`}
      onClick={() => {
        onEditClick('other-experience');
      }}
    >
      {t('common:button.edit')}
    </AnchorButton>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-edit`}
      href={`/profile/${profile.profileId}/other-experience`}
    >
      {t('common:button.edit')}
    </DuoLink>
  );
  const commonButton = isAdmin ? (
    <Button
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-add`}
      onClick={() => {
        onEditClick('other-experience');
      }}
      outlined="true"
    >
      {t('common:profile.otherExperience.add')}
    </Button>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-add`}
      href={`/profile/${profile.profileId}/other-experience`}
    >
      <Button outlined="true">{t('common:profile.otherExperience.add')}</Button>
    </DuoLink>
  );
  const otherExperiencesIcon = openSection === true ? 'chevron-open-thick' : 'chevron-closed-thick';

  return (
    <UserInfoContainer isPreview={isPreview} data-testid="profile-other-experience-container">
      {profile.otherExperiences ? (
        <UserInfoSection>
          <UserInfoHeadingContainer isPreview={isPreview}>
            <UserInfoHeading isPreview={isPreview}>{t('common:form.title.otherExperience')}</UserInfoHeading>
            {!isPreview && otherExperiencesButton}
            {isPreview && (
              <StyledIcon
                data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-${otherExperiencesIcon}-icon`}
                name={otherExperiencesIcon}
                onClick={() => setSectionState(!openSection)}
              />
            )}
          </UserInfoHeadingContainer>
          {openSection && (
            <WrappedTextContainer ref={contentRef} data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-info`}>
              {profile.otherExperiences}
            </WrappedTextContainer>
          )}
        </UserInfoSection>
      ) : (
        !isPreview && (
          <div>
            <InfoHeading>{t('common:form.title.otherExperience')}</InfoHeading>
            <UserInfoBtnContainer>{commonButton}</UserInfoBtnContainer>
          </div>
        )
      )}
    </UserInfoContainer>
  );
};

export default OtherExperience;
